import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { usePermissions } from '../../shared/permissions';
import { useConfirm } from '../../shared/ConfirmContext';
import { useSuppliers, useCreateSupplier, useUpdateSupplier, useDeleteSupplier } from '../../entities/supplier';
import type { Supplier, SupplierInput } from '../../entities/supplier';

const INITIAL_FORM_DATA: SupplierInput = { name: '', email: '', phone: '', notes: '' };

type ModalMode = 'add' | 'edit' | null;

const SupplierManagement = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState<SupplierInput>(INITIAL_FORM_DATA);
  const [error, setError] = useState<string | null>(null);

  const { data: suppliers = [], isLoading: loading } = useSuppliers({ ordering: 'name' });
  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();
  const deleteMutation = useDeleteSupplier();

  const resetModalState = () => {
    setModalMode(null);
    setSelectedSupplier(null);
    setFormData(INITIAL_FORM_DATA);
  };

  const openAddModal = () => { setFormData(INITIAL_FORM_DATA); setModalMode('add'); };
  const openEditModal = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name,
      email: supplier.email || '',
      phone: supplier.phone || '',
      notes: supplier.notes || '',
    });
    setModalMode('edit');
  };

  const handleDelete = async (supplier: Supplier) => {
    if (!await confirm(t('warehouses.suppliers.confirmDelete', { name: supplier.name }))) return;
    deleteMutation.mutate(supplier.id, { onError: () => setError(t('warehouses.suppliers.deleteError')) });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const onError = () => setError(t('warehouses.suppliers.saveError'));
    if (modalMode === 'edit' && selectedSupplier) {
      updateMutation.mutate({ id: selectedSupplier.id, data: formData }, { onSuccess: resetModalState, onError });
    } else {
      createMutation.mutate(formData, { onSuccess: resetModalState, onError });
    }
  };

  const submitting = createMutation.isPending || updateMutation.isPending;

  return (
    <PageShell
      pretitle={t('warehouses.title')}
      title={t('warehouses.suppliers.title')}
      description={t('warehouses.suppliers.subtitle')}
      actions={
        <>
          <Link to="/warehouses/products" className="btn btn-outline-secondary">
            <i className="ti ti-arrow-left me-2"></i>
            {t('common.back')}
          </Link>
          {canManage && (
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="ti ti-plus me-2"></i>
              {t('warehouses.suppliers.add')}
            </button>
          )}
        </>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}

      {loading ? (
        <PageSection><CenteredLoader /></PageSection>
      ) : (
        <PageSection
          title={t('warehouses.suppliers.title')}
          description={t('warehouses.suppliers.sectionDescription')}
          bodyClassName="p-0"
        >
          {suppliers.length === 0 ? (
            <EmptyState
              title={t('warehouses.suppliers.emptyTitle')}
              message={t('warehouses.suppliers.emptyMessage')}
            />
          ) : (
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>{t('warehouses.suppliers.name')}</th>
                    <th>{t('warehouses.suppliers.email')}</th>
                    <th>{t('warehouses.suppliers.phone')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td>{s.email || '—'}</td>
                      <td>{s.phone || '—'}</td>
                      <td className="text-end">
                        {canManage && (
                          <div className="btn-list flex-nowrap justify-content-end">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => openEditModal(s)}>
                              <i className="ti ti-edit"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(s)}>
                              <i className="ti ti-trash"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PageSection>
      )}

      <Modal show={!!modalMode} onHide={resetModalState}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalMode === 'edit' ? t('warehouses.suppliers.edit') : t('warehouses.suppliers.add')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="required">{t('warehouses.suppliers.name')}</Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('warehouses.suppliers.email')}</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('warehouses.suppliers.phone')}</Form.Label>
              <Form.Control
                value={formData.phone}
                onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('warehouses.suppliers.notes')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={resetModalState}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? <Spinner size="sm" className="me-1" /> : null}
              {t('common.save')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default SupplierManagement;
